import React, { useState } from 'react';
import { getInstitutionDomain } from './institutionDomains';

// A tiny greyscale logo used in the country panel so people can visually
// spot their bank. Uses Google's public favicon service (no key, no
// personal data leaves the browser — the request is scoped to a
// well-known public brand domain).
//
// If the favicon fails to load (or we don't ship a domain for that
// institution yet) we quietly fall back to a coloured letter avatar so
// the row never renders empty.

// Deterministic pastel colour so the same bank always gets the same
// fallback tint — helps returning users pattern-match at a glance.
const AVATAR_COLOURS = [
  'bg-slate-200 text-slate-700',
  'bg-emerald-100 text-emerald-800',
  'bg-blue-100 text-blue-800',
  'bg-amber-100 text-amber-800',
  'bg-purple-100 text-purple-800',
  'bg-rose-100 text-rose-800',
  'bg-teal-100 text-teal-800',
  'bg-indigo-100 text-indigo-800',
];

const _hashIndex = (key, mod) => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % mod;
};

const LetterAvatar = ({ name, institutionId }) => {
  const letter = (name || '?').trim().charAt(0).toUpperCase();
  const colour = AVATAR_COLOURS[_hashIndex(institutionId || name || '', AVATAR_COLOURS.length)];
  return (
    <span
      className={`w-5 h-5 rounded-sm flex items-center justify-center text-[10px] font-semibold shrink-0 ${colour}`}
      aria-hidden="true"
      data-testid="institution-logo-fallback"
    >
      {letter}
    </span>
  );
};

const InstitutionLogo = ({ institutionId, name }) => {
  const [failed, setFailed] = useState(false);
  const domain = getInstitutionDomain(institutionId);
  if (!domain || failed) {
    return <LetterAvatar name={name} institutionId={institutionId} />;
  }
  // Google's public favicon endpoint — free, no auth, cached by the browser.
  const src = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  return (
    <img
      src={src}
      alt=""
      width={20}
      height={20}
      loading="lazy"
      onError={() => setFailed(true)}
      className="w-5 h-5 rounded-sm object-contain shrink-0"
      style={{ filter: 'grayscale(100%)', opacity: 0.85 }}
      data-testid="institution-logo-img"
    />
  );
};

export default InstitutionLogo;
